import { falTimes } from 'shared/@generated/icons/falTimes';
import { Pressable, ScrollView, TouchableOpacity, View } from 'react-native';
import {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { Props } from 'shared/@types';
import { Icon } from 'shared/components/icon';
import { ModalHeader } from 'shared/components/modal-header';
import { Text } from 'shared/components/text';
import Modal from 'shared/components/lazy-modal.web';
import { useDevice } from 'shared/library/use-device';
import { BaseModalProps, BaseModalRef } from 'shared/components/base-modal';
import clsx from 'clsx';
import { lockScroll, unlockScroll } from 'shared/library/scroll-lock';
import { useTranslation } from 'react-i18next';

type FullscreenModalProps = Props<
  BaseModalProps & {
    headerTitle?: string;
    description?: ReactNode;
    headerLeft?: (props: BaseModalRef) => ReactNode;
    headerRight?: () => ReactNode;
    footer?: (props: BaseModalRef) => ReactNode;
    centered?: boolean;
    showHeader?: boolean;
    showCloseButton?: boolean;
    closeAccessibilityLabel?: string;
    closeDisabled?: boolean;
    scrollable?: boolean;
    contentTestID?: string;
    animationInTiming?: number;
    animationOutTiming?: number;
    onRequestClose?: () => boolean | void;
  }
>;

export const FullscreenModal = forwardRef<BaseModalRef, FullscreenModalProps>(
  (
    {
      trigger,
      closeOnBackdropPress = true,
      onClose,
      children,
      headerTitle,
      description,
      headerLeft,
      headerRight,
      footer,
      centered = false,
      showHeader = true,
      showCloseButton = showHeader,
      closeAccessibilityLabel,
      closeDisabled = false,
      scrollable = true,
      contentTestID,
      animationInTiming,
      animationOutTiming,
      onRequestClose,
      className,
    },
    ref,
  ) => {
    const { t } = useTranslation('common');
    const { widerThanMd } = useDevice();
    const [isMounted, setIsMounted] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const modalHasOpened = useRef(false);

    const animationIn = useMemo(
      () => (!widerThanMd ? 'slideInUp' : centered ? 'centeredIn' : 'slideInLeft'),
      [widerThanMd, centered],
    );

    const animationOut = useMemo(
      () => (!widerThanMd ? 'slideOutDown' : centered ? 'centeredOut' : 'slideOutLeft'),
      [widerThanMd, centered],
    );

    function open() {
      setIsMounted(true);
      setShowModal(true);
    }

    function close() {
      onClose?.();
      setShowModal(false);
      // A pending lazy import has no exit animation or onModalHide callback.
      if (!modalHasOpened.current) setIsMounted(false);
    }

    function requestClose() {
      if (closeDisabled || onRequestClose?.() === false) {
        return;
      }

      close();
    }

    useEffect(() => {
      if (!isMounted) return;

      lockScroll();
      return unlockScroll;
    }, [isMounted]);

    const modalRef: BaseModalRef = {
      open,
      close,
    };

    useImperativeHandle(ref, () => modalRef);

    const hasCustomMaxWidth = /(^|\s)(?:[a-z-]+:)*max-w-/.test(className ?? '');
    const hasCustomHeight = /(^|\s)(?:[a-z-]+:)*h-/.test(className ?? '');
    const hasCustomMaxHeight = /(^|\s)(?:[a-z-]+:)*max-h-/.test(className ?? '');
    const closeButton = showCloseButton && (
      <TouchableOpacity
        onPress={requestClose}
        disabled={closeDisabled}
        accessibilityState={{ disabled: closeDisabled }}
        className={'hover:bg-brand-cream-dark rounded p-2 transition-colors'}
        accessibilityRole={'button'}
        accessibilityLabel={closeAccessibilityLabel ?? t('close')}
        testID="modal-close"
      >
        <Icon icon={falTimes} className={'text-brand-brown-dark'} />
      </TouchableOpacity>
    );

    return (
      <>
        {trigger?.(modalRef)}

        {isMounted && (
          <Modal
            isVisible={showModal}
            onLoadingCancel={closeDisabled ? undefined : requestClose}
            animationIn={animationIn}
            animationOut={animationOut}
            animationInTiming={animationInTiming}
            animationOutTiming={animationOutTiming}
            hideModalContentWhileAnimating={true}
            onBackButtonPress={() => closeOnBackdropPress && requestClose()}
            onBackdropPress={() => closeOnBackdropPress && requestClose()}
            onModalWillShow={() => {
              modalHasOpened.current = true;
            }}
            onModalHide={() => {
              modalHasOpened.current = false;
              setIsMounted(false);
            }}
            backdropOpacity={1}
            customBackdrop={
              <Pressable
                onPress={() => closeOnBackdropPress && requestClose()}
                className={
                  'bg-brand-brown-dark/70 fixed inset-0 h-screen w-screen cursor-default outline-none'
                }
                testID={'modal-backdrop'}
              />
            }
            style={{ margin: 0 }}
          >
            <View
              className={clsx(
                'h-dvh w-full',
                !hasCustomMaxWidth && 'md:max-w-lg',
                {
                  'md:h-auto': centered && !hasCustomHeight,
                  'md:max-h-[96dvh]': centered && !hasCustomMaxHeight,
                  'md:self-center md:p-3': centered,
                },
                className,
              )}
            >
              <View
                className={clsx(
                  'bg-brand-cream-light shadow-brand-brown-dark/20 size-full shadow-lg',
                  {
                    'overflow-hidden md:rounded-lg': centered,
                  },
                )}
                testID={contentTestID}
              >
                {showHeader && (
                  <ModalHeader
                    title={headerTitle}
                    left={headerLeft?.(modalRef)}
                    right={headerRight?.()}
                    onClose={showCloseButton ? requestClose : undefined}
                    closeDisabled={closeDisabled}
                    closeAccessibilityLabel={closeAccessibilityLabel}
                  />
                )}

                {scrollable ? (
                  <ScrollView className={clsx('p-3 md:px-5 md:py-4')}>
                    {!!description && (
                      <Text className={'text-brand-brown-dark mb-6'}>{description}</Text>
                    )}
                    {children}
                  </ScrollView>
                ) : (
                  <>
                    {!!description && (
                      <View className={'px-3 pt-3 md:px-5 md:pt-4'}>
                        <Text className={'text-brand-brown-dark'}>{description}</Text>
                      </View>
                    )}
                    {children}
                  </>
                )}

                {footer && (
                  <View className={'border-brand-brown-dark/10 shrink-0 border-t p-3 md:px-5'}>
                    <View
                      pointerEvents={'none'}
                      aria-hidden={true}
                      className={
                        'from-brand-cream-light/0 to-brand-cream-light absolute inset-x-0 bottom-full mb-px h-6 bg-gradient-to-b'
                      }
                    />

                    {footer({ open, close })}
                  </View>
                )}

                {!showHeader && showCloseButton && (
                  <View className={'absolute right-3 top-4 z-10'}>{closeButton}</View>
                )}
              </View>
            </View>
          </Modal>
        )}
      </>
    );
  },
);

FullscreenModal.displayName = 'FullscreenModal';
